import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { SiteShell } from "@/project/components/site-shell";
import { Calendar, MapPin, Users, Clock, CheckCircle2, ChevronRight, Star, Heart } from "lucide-react";

export const Route = createFileRoute("/events")({
  head: () => ({ meta: [
    { title: "Events & Volunteer — Daan Amrut Foundation" },
    { name: "description", content: "Join seva camps, donation drives and vet visit days. Register as a volunteer today." },
  ]}),
  component: Events,
});

const EVENTS = [
  {
    id: "e1",
    title: "Paryushan Fodder Mega Drive",
    type: "Donation Drive",
    date: "2026-08-25",
    time: "8:00 AM – 6:00 PM",
    location: "Sarkhej Panjrapol, Ahmedabad",
    city: "Ahmedabad",
    institution: "Sarkhej Panjrapol",
    slots: 40,
    filled: 27,
    description: "Join thousands of Jain families in donating green fodder, dry straw, and medicines during Paryushan. Physical collection point at Sarkhej Panjrapol.",
    tags: ["Fodder", "Paryushan", "Walk-in"],
    featured: true,
    image: "https://images.unsplash.com/photo-1584464367415-ba9c05ef0282?w=600&q=80",
  },
  {
    id: "e2",
    title: "Free Vet Camp — Bird Hospital",
    type: "Vet Camp",
    date: "2026-09-07",
    time: "9:00 AM – 2:00 PM",
    location: "Vadodara Bird Hospital, Fatehpura",
    city: "Vadodara",
    institution: "Vadodara Bird Hospital",
    slots: 20,
    filled: 8,
    description: "Volunteer vets and vet students welcome. Assist in treating injured birds, administering medicines, and documenting health records.",
    tags: ["Birds", "Medical", "Volunteers Needed"],
    featured: false,
    image: "https://images.unsplash.com/photo-1444927714506-8492d94b4e3d?w=600&q=80",
  },
  {
    id: "e3",
    title: "Awareness Walk — Stray Dog Welfare",
    type: "Awareness",
    date: "2026-09-15",
    time: "6:30 AM – 9:00 AM",
    location: "Law Garden, Ahmedabad",
    city: "Ahmedabad",
    institution: "Ahmedabad SPCA",
    slots: 100,
    filled: 62,
    description: "A morning walk to raise awareness about stray dog welfare. T-shirts, placards, and refreshments provided. Bring friends and family.",
    tags: ["Dogs", "Awareness", "Family-Friendly"],
    featured: false,
    image: "https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=600&q=80",
  },
  {
    id: "e4",
    title: "Daan Amrut Annual Gaushala Meet",
    type: "Conference",
    date: "2026-10-02",
    time: "10:00 AM – 5:00 PM",
    location: "Tagore Hall, Ahmedabad",
    city: "Ahmedabad",
    institution: "Daan Amrut Foundation",
    slots: 200,
    filled: 115,
    description: "Annual gathering of all partner gaushalas, donors, and volunteers. Panel discussions, impact showcases, and networking. Register early.",
    tags: ["Conference", "Networking", "All Welcome"],
    featured: true,
    image: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=600&q=80",
  },
  {
    id: "e5",
    title: "Navratri Animal Seva Camp",
    type: "Seva Camp",
    date: "2026-10-10",
    time: "7:00 AM – 12:00 PM",
    location: "Rajkot Panjrapol, Rajkot",
    city: "Rajkot",
    institution: "Rajkot Panjrapol",
    slots: 60,
    filled: 12,
    description: "Celebrate Navratri through seva — help clean animal pens, distribute fodder, and assist institution staff. All ages welcome.",
    tags: ["Navratri", "Seva", "All Ages"],
    featured: false,
    image: "https://images.unsplash.com/photo-1516684732162-798a0062be99?w=600&q=80",
  },
];

const EVENT_TYPES = ["All", "Seva Camp", "Donation Drive", "Vet Camp", "Awareness", "Conference"];
const CITIES = ["All Cities", "Ahmedabad", "Vadodara", "Surat", "Rajkot"];

const TYPE_COLORS: Record<string, string> = {
  "Seva Camp": "bg-green-100 text-green-700",
  "Donation Drive": "bg-[var(--saffron)]/20 text-[var(--maroon-deep)]",
  "Vet Camp": "bg-blue-100 text-blue-700",
  "Awareness": "bg-purple-100 text-purple-700",
  "Conference": "bg-slate-100 text-slate-700",
};

function Events() {
  const [type, setType] = useState("All");
  const [city, setCity] = useState("All Cities");
  const [registered, setRegistered] = useState<string[]>([]);

  const filtered = EVENTS.filter((e) => {
    const matchType = type === "All" || e.type === type;
    const matchCity = city === "All Cities" || e.city === city;
    return matchType && matchCity;
  });

  const featured = filtered.filter((e) => e.featured);
  const regular = filtered.filter((e) => !e.featured);

  const toggle = (id: string) =>
    setRegistered((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  return (
    <SiteShell>
      {/* ── Hero ── */}
      <section className="border-b border-border/60 bg-gradient-to-br from-[var(--cream)] to-[var(--cream-2)]">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--saffron-deep)]">Events & Volunteering</p>
          <h1 className="mt-2 font-display text-3xl font-bold sm:text-4xl text-[var(--maroon-deep)]">Join the seva. Be the change.</h1>
          <p className="mt-3 max-w-2xl text-foreground/70">Seva camps, donation drives, vet visits, and awareness walks — find events near you and register as a volunteer.</p>

          {/* Filters */}
          <div className="mt-6 flex flex-wrap gap-3">
            <div className="flex flex-wrap gap-2">
              {EVENT_TYPES.map((t) => (
                <button key={t} onClick={() => setType(t)}
                  className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition ${type === t ? "border-[var(--maroon)] bg-[var(--maroon)] text-white" : "border-border bg-white text-foreground/70 hover:border-[var(--maroon)]/40"}`}>
                  {t}
                </button>
              ))}
            </div>
            <select value={city} onChange={(e) => setCity(e.target.value)}
              className="rounded-full border border-border bg-white px-4 py-1.5 text-xs font-medium text-foreground/70 outline-none">
              {CITIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 space-y-12">
        {/* ── Featured ── */}
        {featured.length > 0 && (
          <div>
            <h2 className="mb-5 font-display text-xl font-bold text-[var(--maroon-deep)] flex items-center gap-2">
              <Star className="h-5 w-5 text-[var(--saffron-deep)] fill-[var(--saffron-deep)]" /> Featured Events
            </h2>
            <div className="grid gap-6 md:grid-cols-2">
              {featured.map((e) => (
                <EventCard key={e.id} event={e} registered={registered.includes(e.id)} onToggle={() => toggle(e.id)} />
              ))}
            </div>
          </div>
        )}

        {/* ── All events ── */}
        {regular.length > 0 && (
          <div>
            <h2 className="mb-5 font-display text-xl font-bold text-[var(--maroon-deep)]">Upcoming Events</h2>
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {regular.map((e) => (
                <EventCard key={e.id} event={e} registered={registered.includes(e.id)} onToggle={() => toggle(e.id)} compact />
              ))}
            </div>
          </div>
        )}

        {filtered.length === 0 && (
          <div className="py-20 text-center text-foreground/50">No events match your filters.</div>
        )}

        {/* ── Become a volunteer ── */}
        <div className="rounded-3xl bg-gradient-to-br from-[var(--maroon)] to-[var(--maroon-deep)] p-8 sm:p-10 text-white">
          <div className="grid gap-6 sm:grid-cols-2 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-[var(--saffron)]">Join the network</p>
              <h3 className="mt-2 font-display text-2xl font-bold">Become a Seva Volunteer</h3>
              <p className="mt-2 text-sm text-white/80">Register once and get notified about volunteer opportunities near you. No prior experience needed — just a willing heart.</p>
            </div>
            <div className="flex flex-col sm:items-end gap-3">
              <Link to="/volunteer"
                className="rounded-full bg-[var(--saffron)] px-6 py-3 text-sm font-bold text-[var(--maroon-deep)] hover:bg-white transition text-center">
                Register as Volunteer
              </Link>
              <p className="text-xs text-white/60">Join 2,400+ volunteers across Gujarat</p>
            </div>
          </div>
        </div>
      </div>
    </SiteShell>
  );
}

function EventCard({ event: e, registered, onToggle, compact = false }: {
  event: typeof EVENTS[0];
  registered: boolean;
  onToggle: () => void;
  compact?: boolean;
}) {
  const pct = Math.round((e.filled / e.slots) * 100);
  const full = pct >= 100;

  return (
    <div className={`overflow-hidden rounded-2xl border border-border/60 bg-card shadow-[var(--shadow-soft)] transition hover:shadow-[var(--shadow-card)] flex flex-col`}>
      {!compact && (
        <div className="relative">
          <img src={e.image} alt="" className="aspect-[2/1] w-full object-cover" />
          <span className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-[10px] font-bold ${TYPE_COLORS[e.type] ?? "bg-slate-100 text-slate-700"}`}>
            {e.type}
          </span>
        </div>
      )}
      <div className="flex flex-1 flex-col p-5">
        {compact && (
          <span className={`mb-2 inline-block rounded-full px-2.5 py-1 text-[10px] font-bold w-fit ${TYPE_COLORS[e.type] ?? "bg-slate-100 text-slate-700"}`}>
            {e.type}
          </span>
        )}
        <h3 className="font-display text-base font-bold leading-snug">{e.title}</h3>
        <div className="mt-2 space-y-1.5 text-xs text-foreground/60">
          <div className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />
            {new Date(e.date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })} · {e.time}
          </div>
          <div className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{e.location}</div>
        </div>
        {!compact && <p className="mt-3 text-sm text-foreground/70 line-clamp-2">{e.description}</p>}

        {/* Volunteer slots */}
        <div className="mt-4">
          <div className="mb-1 flex justify-between text-xs text-foreground/60">
            <span className="flex items-center gap-1"><Users className="h-3 w-3" />{e.filled} / {e.slots} slots filled</span>
            <span>{pct}%</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-border/50">
            <div className={`h-full rounded-full ${full ? "bg-red-500" : "bg-[var(--maroon)]"}`} style={{ width: `${Math.min(100, pct)}%` }} />
          </div>
        </div>

        <div className="mt-4 flex gap-2">
          <button
            onClick={onToggle}
            disabled={full && !registered}
            className={`flex-1 rounded-full py-2.5 text-xs font-bold transition flex items-center justify-center gap-1.5 ${
              registered
                ? "bg-green-600 text-white hover:bg-green-700"
                : full
                  ? "bg-border/50 text-foreground/40 cursor-not-allowed"
                  : "bg-[var(--maroon)] text-white hover:bg-[var(--maroon-deep)]"
            }`}
          >
            {registered ? <><CheckCircle2 className="h-3.5 w-3.5" /> Registered</> : full ? "Slots Full" : <><Heart className="h-3.5 w-3.5" /> Register</>}
          </button>
          <button className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-border hover:bg-muted transition text-[11px]">
            <span title="Add to calendar">📅</span>
          </button>
        </div>

        {/* Tags */}
        <div className="mt-3 flex flex-wrap gap-1.5">
          {e.tags.map((t) => (
            <span key={t} className="rounded-full bg-muted/40 px-2 py-0.5 text-[10px] text-foreground/60">{t}</span>
          ))}
        </div>
      </div>
    </div>
  );
}